import { useAsyncData } from '#imports'
import { type MaybeRefOrGetter, toValue } from 'vue'

import type { Price } from '@backmarket/http-api/src/api-models/Price'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { Country } from '@backmarket/http-api/src/standards/Country'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import type {
  GetPaymentMethodsOptions,
  PaymentTenant,
} from '../../../api/getPaymentMethods/getPaymentMethods.specs'
import { usePaymentMethods } from '../../../api/getPaymentMethods/usePaymentMethods.api'
import { filterABTestedPaymentMethods } from '../helpers/filterABTestedPaymentMethods'

export type PaymentMethodsFeatures = 'bouygues_in_store'[]

/**
 * Returns payment methods for the current country that correspond to the given listings, bag price ...
 * Prefer the use of the cached version `useMarketPaymentMethods` when possible
 *
 * @example
 * const { data: paymentMethods } = await useMarketPaymentMethodsFor({
 *  listings: ['123', '456'],
 *  bagPrice: { amount: '99.99', currency: 'EUR' },
 * })
 */
export const useMarketPaymentMethodsFor = ({
  listings,
  bagPrice,
  hasInsurance,
  tokenization,
  tenant,
  customMethodFilter,
  features,
  billingCountry,
}: {
  listings?: MaybeRefOrGetter<string[]>
  bagPrice?: MaybeRefOrGetter<Price | undefined>
  hasInsurance?: MaybeRefOrGetter<boolean>

  // TODO [PAYIN-5884]: Merge tenant and features into a single "payment flow" option
  tenant?: PaymentTenant
  features?: PaymentMethodsFeatures

  /**
   * When true, only the payment methods that support tokenization will be returned
   */
  tokenization?: MaybeRefOrGetter<boolean>

  /**
   * Apply a custom filter on the payment methods returned by the API.
   * This was added to allow 'express replacement' flow to only show the card payment methods during the MVP.
   */
  customMethodFilter?: (methods: PaymentMethod[]) => PaymentMethod[]

  /**
   * Country code from the user's billing address, used to filter payment methods by billing country.
   * As of today this is used to filter out Klarna when the billing country is different from the country code.
   * As klarna payment will fail in those cases.
   */
  billingCountry?: MaybeRefOrGetter<Country | undefined>
}) => {
  const { market } = useMarketplace()

  const experiments = useExperiments()

  const getPaymentMethods = usePaymentMethods()

  const reactiveOptions = (): GetPaymentMethodsOptions => ({
    tenant: tenant ?? 'SALES',
    countryCode: market.countryCode,
    billingCountry: toValue(billingCountry),
    listings: toValue(listings),
    bagPrice: toValue(bagPrice),
    insuranceSubscription: toValue(hasInsurance),
    features: toValue(tokenization)
      ? ['tokenizable', ...(features ?? [])]
      : features,
  })

  return useAsyncData(
    // Keys are not reactive. It's important that the options passed are correct from the beginning, especially on the server side for request deduplication.
    // For example, if you pass an empty object as options and then update it, the key will not change.
    // Two different calls that started with the same empty object will have the same key, even if the options are different later on.
    // This situation is unlikely but possible.
    // Also, because of this, it's difficult to cache the data for future requests as `getCachedData` will always receive the same key, even if the options start to be different.
    // It may be fixed in the future: https://github.com/nuxt/nuxt/issues/21532
    getAsyncDataKey('use-market-payment-methods-for', reactiveOptions()),
    () => getPaymentMethods(reactiveOptions()),
    {
      transform: (data) => {
        const filtered = filterABTestedPaymentMethods(
          experiments,
          data.results,
          toValue(bagPrice) || null,
        )
        if (customMethodFilter) {
          return customMethodFilter(filtered)
        }

        return filtered
      },
      dedupe: 'defer',
      watch: [reactiveOptions],
    },
  )
}
